import { Chip, lighten, Stack } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'

import Text, { styles as textStyles } from 'components/common/Text'
import Icon from 'components/Icon'
import { HaltAllAttacksButton } from 'components/navigation/HaltAllAttacksButton'
import { HeaderAlert } from 'components/navigation/HeaderAlert'
import { MainHeaderMenu } from 'components/navigation/menu/MainHeaderMenu'
import { RestrictedTimeWindowHeaderAlert } from 'components/navigation/RestrictedTimeWindowHeaderAlert'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { RbacPrivilege } from 'types/api/rbac/rbac'

export const Header: React.FC = ({ children, ...rest }) => {
  const { isProductionEnvironment, session } = useAuth()
  const isATeamMember = session?.teams?.length > 0
  const canHaltAll = session?.privileges?.includes(RbacPrivilege.HALT_ALL)

  return (
    <>
      <Stack
        component="header"
        sx={[
          {
            background: theme.colors.white[0],
            borderBottom: theme.border.base,
            height: 'var(--header-height)',
            justifyContent: 'center',
            mx: 0,
            mr: 0,
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndices[4]
          },
          (muiTheme) => ({
            [theme.mq[2] as string]: { top: 0 },
            ...(isProductionEnvironment
              ? {
                  backgroundColor: lighten(muiTheme.palette.warning.light, 0.88)
                }
              : { backgroundColor: lighten(theme.colors.gray[0], 0.75) })
          })
        ]}
        {...rest}
      >
        <Stack
          alignItems="center"
          direction="row"
          spacing={1}
          sx={[{ px: 3, py: 2 }, { [theme.mq[2] as string]: { px: 7 } }]}
        >
          {children}
          {isProductionEnvironment && (
            <Chip
              color="warning"
              data-testid="service-list-production-chip"
              label="Production"
            />
          )}
          <HeaderAlert />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              flexGrow: 1,
              justifyContent: 'flex-end'
            }}
          >
            <MainHeaderMenu />
            {isATeamMember && canHaltAll && <HaltAllAttacksButton />}
          </Stack>
        </Stack>
      </Stack>
      <RestrictedTimeWindowHeaderAlert />
    </>
  )
}

export const HeaderLink: React.FC<{ to: string }> = ({
  to,
  children,
  ...rest
}) => {
  return (
    <Link to={to} {...rest} css={{ textDecoration: 'none' }}>
      <Text
        as="h3"
        color="normal"
        css={{
          '&:hover': {
            color: theme.colors.blue[0]
          }
        }}
      >
        {children}
      </Text>
    </Link>
  )
}

// @NOTE We want to use H1 for accessibility but use the styles of an H3
export const Heading: React.FC = (props) => {
  return (
    <Text as="h1" css={textStyles['h3']} data-testid="page-title" {...props} />
  )
}

export const HeaderRightChevron: React.FC = (props) => {
  return (
    <Icon
      css={{
        marginLeft: theme.space[2],
        marginRight: theme.space[2],
        flex: 'none' // Prevent collapsing
      }}
      icon="chevronRight"
      {...props}
    />
  )
}
